import { memoizeFunction } from '@agilepoint/utils-common';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { IItemData, IListViewContentManagerProps } from '../types';

const isKeyAvailableInItems = memoizeFunction((items: any[], key: React.Key, getKey: (item: any) => any) => items.some(item => getKey(item) === key));

interface IUseSelectionInfoOptions extends IListViewContentManagerProps {
    itemsInView: IItemData[];
    isContentReadyToView: boolean;
    getOccupiedSpace: (start: number, end?: number) => number;
}

export function useSelectionInfo(options: IUseSelectionInfoOptions) {
    const {
        items, selectable, selectedKeys, scrollOffset, isHorizontallyAligned, canSelectItem, setScroll,
        getKey, containerDimensions, isContentReadyToView, itemsInView, getOccupiedSpace
    } = options;
    const [selectedKey] = selectedKeys;
    const [isScrollToSelectionPending, setScrollToSelectionState] = useState(selectable === 'single' && isKeyAvailableInItems(items, selectedKey, getKey));
    const isPrevContentReadyToViewRef = useRef(isContentReadyToView);

    const isSelected = useCallback((item: any) => {
        const key = getKey(item);
        return selectable !== 'none' && selectedKeys.includes(key) && canSelectItem(item);
    }, [canSelectItem, getKey, selectable, selectedKeys]);

    const getItemSelectableConfig = useCallback((item: any) => (canSelectItem(item) ? selectable : 'none'), [canSelectItem, selectable]);

    const selectedItemPredicate = useCallback((item: any) => (getKey(item) === selectedKey), [getKey, selectedKey]);

    const shouldScrollToSelection = useCallback(() => {
        if (!isScrollToSelectionPending) {
            return false;
        }

        const itemInfo = itemsInView.find((i) => selectedItemPredicate(i.item));

        if (!itemInfo) {
            return true;
        }

        const { height, width } = containerDimensions;
        const { position: ItemPosition } = itemInfo;
        let itemStartPosition = ItemPosition.top;
        let itemEndPosition = itemStartPosition + ItemPosition.height;
        let viewPortScrollEndPosition = scrollOffset + height;

        if (isHorizontallyAligned) {
            itemStartPosition = ItemPosition.left;
            itemEndPosition = itemStartPosition + ItemPosition.width;
            viewPortScrollEndPosition = scrollOffset + width;
        }

        return (itemStartPosition < scrollOffset || itemEndPosition > viewPortScrollEndPosition);
    }, [containerDimensions, isHorizontallyAligned, isScrollToSelectionPending, itemsInView, scrollOffset, selectedItemPredicate]);

    useLayoutEffect(() => {
        if (isPrevContentReadyToViewRef.current === isContentReadyToView) {
            return;
        }
        if (!shouldScrollToSelection()) {
            setScrollToSelectionState(false);
            return;
        }
        const itemIndex = items.findIndex(selectedItemPredicate);
        const scrollPosition = getOccupiedSpace(0, itemIndex - 1);
        setScroll(scrollPosition);
        setScrollToSelectionState(false);
    }, [getOccupiedSpace, isContentReadyToView, isScrollToSelectionPending, items, selectedItemPredicate, setScroll, shouldScrollToSelection]);

    return { isScrollToSelectionPending, isSelected, getItemSelectableConfig };
}
